import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { UpdatesCard } from './Updates';
import { FeaturesCard } from './FeaturesCard';
import { api, type ChecklistStep, type CustomerDetail, type CustomerSummary, type CustomerTexting, type InviteResult, type SeatRequestView, type StepId } from '../../api/client';
import { Button, Card, Chip, Dialog, Empty, Field, LoadError, Loading, Notice, PageHeader, TextAreaField } from '../../components/ui';
import { dateOnly, monthDay, plural } from '../../lib/format';
import { Icon, type IconName } from '../../lib/icons';
import { useData } from '../../lib/useData';
import { ShareLink, seatRequestText } from '../tenant/Team';
import { RegistrationStaffCard } from './TextingRegistrationStaff';
import { CallsAbroadCard } from './CallsAbroadCard';
import { TeampaceCard } from './TeampaceCard';

const STATUS = {
  onboarding: { label: 'Setting up', tone: 'brand' as const },
  active: { label: 'Live', tone: 'ok' as const },
  suspended: { label: 'Suspended', tone: 'crit' as const },
  // 1.13.0 (2717): the company's service ended; Call history, Storage and Billing stay.
  archive_only: { label: 'Archive only', tone: 'neutral' as const }
};

/** Every customer, plus tenants on the phone system that aren't customers in Stitch yet. */
export function AdminCustomers() {
  const data = useData(() => api.customers(), []);
  const asks = useData(() => api.seatRequests('waiting'), []);
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const [picked, setPicked] = useState<string[]>([]);
  const [importing, setImporting] = useState(false);
  const [importSetUp, setImportSetUp] = useState(true);
  const [error, setError] = useState('');
  const list = data.data?.customers ?? [];
  const unlinked = data.data?.unlinked ?? [];
  const setting = list.filter((c) => c.status === 'onboarding');

  return (
    <>
      <PageHeader
        eyebrow={data.data ? `${plural(list.length, 'customer')}${setting.length ? ` · ${setting.length} being set up` : ''}` : 'Stitch Admin'}
        title="Customers"
        actions={
          <Button variant="primary" icon="plus" onClick={() => setCreating(true)}>
            New customer
          </Button>
        }
      />
      <div className="content">
        {error && <Notice tone="crit">{error}</Notice>}
        {asks.data && asks.data.requests.length > 0 && (
          <Card title="Asking for more messaging-only people" action={<Chip tone="brand">{plural(asks.data.waiting, 'waiting', 'waiting')}</Chip>}>
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th scope="col">Customer</th>
                    <th scope="col">Asked for</th>
                    <th scope="col" className="hide-sm">
                      Note
                    </th>
                    <th scope="col">Asked</th>
                  </tr>
                </thead>
                <tbody>
                  {asks.data.requests.map((r) => (
                    <tr key={r.id} className="clickable" onClick={() => navigate(`/admin/customers/${r.customer.id}`)}>
                      <td>
                        <Link to={`/admin/customers/${r.customer.id}`} className="cell-stack plain-link" onClick={(e) => e.stopPropagation()}>
                          <strong>{r.customer.name}</strong>
                          <small className="muted">{r.seats ? `${r.seats.used} of ${r.seats.allowance} taken` : (r.customer.tenant ?? '')}</small>
                        </Link>
                      </td>
                      <td>
                        {r.more} more
                        {r.seats ? <span className="muted"> ({r.seats.allowance + r.more} in all)</span> : null}
                      </td>
                      <td className="hide-sm">{r.note ? <span className="muted">{r.note.length > 90 ? `${r.note.slice(0, 89)}…` : r.note}</span> : <span className="muted">–</span>}</td>
                      <td>
                        <span className="cell-stack">
                          <span>{monthDay(r.askedAt)}</span>
                          <small className="muted">{r.askedBy}</small>
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
        <Card>
          {data.error ? (
            <LoadError error={data.error} retry={data.reload} />
          ) : !data.data ? (
            <Loading label="Loading customers" />
          ) : list.length === 0 ? (
            <Empty title="No customers yet">Start a new customer, or bring in the tenants already on the phone system below.</Empty>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th scope="col">Customer</th>
                    <th scope="col">Status</th>
                    <th scope="col" className="hide-sm">
                      Contact
                    </th>
                    <th scope="col" className="hide-sm">
                      Since
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((c) => (
                    <tr key={c.id} className="clickable" onClick={() => navigate(`/admin/customers/${c.id}`)}>
                      <td>
                        <Link to={`/admin/customers/${c.id}`} className="cell-stack plain-link" onClick={(e) => e.stopPropagation()}>
                          <strong>{c.name}</strong>
                          <small className="muted mono">{c.tenant ?? 'No tenant yet'}</small>
                        </Link>
                      </td>
                      <td>
                        <Chip small tone={STATUS[c.status].tone}>
                          {STATUS[c.status].label}
                        </Chip>
                      </td>
                      <td className="hide-sm">{c.contactEmail ? `${c.contactName || ''} ${c.contactName ? '·' : ''} ${c.contactEmail}` : <span className="muted">–</span>}</td>
                      <td className="hide-sm">{dateOnly(c.liveAt ?? c.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {unlinked.length > 0 && (
          <Card
            title="On the phone system, not in Stitch yet"
            action={
              <Button
                size="small"
                variant="primary"
                disabled={picked.length === 0 || importing}
                onClick={async () => {
                  setImporting(true);
                  setError('');
                  try {
                    await api.importCustomers(picked, importSetUp);
                    setPicked([]);
                    data.reload();
                  } catch (e) {
                    setError(e instanceof Error ? e.message : String(e));
                  } finally {
                    setImporting(false);
                  }
                }}
              >
                Bring in {picked.length ? plural(picked.length, 'tenant') : ''}
              </Button>
            }
          >
            <div className="card-body stack">
              <p className="muted" style={{ margin: 0 }}>
                These tenants already work. Bringing them in makes them live customers here, so you can invite their admins.
              </p>
              <label className="check-row">
                <input type="checkbox" checked={importSetUp} onChange={(e) => setImportSetUp(e.target.checked)} />
                <span className="cell-stack">
                  <span>Set each one up with every update now (recommended)</span>
                  <small className="muted">App calling, voicemail emails from Stitch, old voicemail: make room, and Patch rings. Leave it for later on Updates.</small>
                </span>
              </label>
              <div className="check-list">
                <label className="check">
                  <input type="checkbox" checked={picked.length === unlinked.length} onChange={(e) => setPicked(e.target.checked ? unlinked.map((t) => t.domain) : [])} />
                  <strong>All of them</strong>
                </label>
                {unlinked.map((t) => (
                  <label key={t.domain} className="check">
                    <input
                      type="checkbox"
                      checked={picked.includes(t.domain)}
                      onChange={(e) => setPicked((p) => (e.target.checked ? [...p, t.domain] : p.filter((x) => x !== t.domain)))}
                    />
                    <span className="cell-stack">
                      <span>{t.name}</span>
                      <small className="muted mono">
                        {t.domain}
                        {t.extensionCount !== undefined ? ` · ${plural(t.extensionCount, 'extension')}` : ''}
                      </small>
                    </span>
                  </label>
                ))}
              </div>
            </div>
          </Card>
        )}
      </div>
      {creating && (
        <NewCustomer
          tenants={unlinked}
          onClose={() => setCreating(false)}
          onCreate={async (body) => {
            const r = await api.createCustomer(body);
            navigate(`/admin/customers/${r.customer.id}`);
          }}
        />
      )}
    </>
  );
}

function NewCustomer({
  tenants,
  onClose,
  onCreate
}: {
  tenants: { domain: string; name: string }[];
  onClose: () => void;
  onCreate: (body: { name: string; contactName: string; contactEmail: string; domain?: string; setUp: boolean }) => Promise<void>;
}) {
  const [name, setName] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [domain, setDomain] = useState('');
  const [setUp, setSetUp] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  return (
    <Dialog
      open
      onClose={onClose}
      title="New customer"
      subtitle="Start with the company. The rest is a checklist you can come back to."
      foot={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!name.trim() || busy}
            onClick={async () => {
              setBusy(true);
              setError('');
              try {
                await onCreate({ name: name.trim(), contactName: contactName.trim(), contactEmail: contactEmail.trim(), ...(domain ? { domain } : {}), setUp });
              } catch (e) {
                setError(e instanceof Error ? e.message : String(e));
                setBusy(false);
              }
            }}
          >
            Create
          </Button>
        </>
      }
    >
      <Field label="Company name" value={name} onChange={(e) => setName(e.target.value)} data-autofocus />
      <Field label="Contact name" value={contactName} onChange={(e) => setContactName(e.target.value)} autoComplete="off" />
      <Field label="Contact email" type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} autoComplete="off" />
      <div className="field">
        <label htmlFor="new-tenant">Phone system tenant</label>
        <select id="new-tenant" className="input" value={domain} onChange={(e) => setDomain(e.target.value)}>
          <option value="">Not created yet</option>
          {tenants.map((t) => (
            <option key={t.domain} value={t.domain}>
              {t.name} ({t.domain})
            </option>
          ))}
        </select>
        <span className="hint">Not on the phone system yet? Leave it as Not created yet: the setup checklist creates it when Stitch may create tenants. Otherwise create it in the phone system’s admin, and it shows up here.</span>
      </div>
      {domain && (
        <label className="check-row">
          <input type="checkbox" checked={setUp} onChange={(e) => setSetUp(e.target.checked)} />
          <span className="cell-stack">
            <span>Set it up with every update now (recommended)</span>
            <small className="muted">App calling, voicemail emails from Stitch, old voicemail: make room, and Patch rings. Leave it for later on its Updates card.</small>
          </span>
        </label>
      )}
      {error && <Notice tone="crit">{error}</Notice>}
    </Dialog>
  );
}

const STEP_ICON: Record<StepId, IconName> = {
  company: 'building',
  tenant: 'server',
  numbers: 'hash',
  people: 'users',
  phones: 'device',
  admin: 'key',
  live: 'flag'
};

/** One customer: the onboarding checklist, each step with what to do next. */
export function AdminCustomer() {
  const { id = '' } = useParams();
  const data = useData(() => api.customer(id), [id]);
  const tenants = useData(() => api.customers().then((r) => r.unlinked), []);
  const [error, setError] = useState('');
  const [invite, setInvite] = useState<InviteResult | null>(null);
  const [editing, setEditing] = useState(false);
  const [checklist, setChecklist] = useState<ChecklistStep[] | null>(null);

  if (data.error) {
    return (
      <div className="content">
        <Card>
          <LoadError error={data.error} retry={data.reload} />
        </Card>
      </div>
    );
  }
  if (!data.data) return <Loading label="Loading the customer" />;
  const c = data.data.customer;
  const steps = checklist ?? data.data.checklist;
  const portal = c.tenant ? `/t/${encodeURIComponent(c.tenant)}` : '';
  const done = steps.filter((s) => s.state === 'done' || s.state === 'skipped').length;

  async function run(fn: () => Promise<{ checklist: ChecklistStep[] } | unknown>) {
    setError('');
    try {
      const r = await fn();
      if (r && typeof r === 'object' && 'checklist' in r) setChecklist((r as { checklist: ChecklistStep[] }).checklist);
      data.reload();
      tenants.reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  const action = (s: ChecklistStep) => {
    switch (s.step) {
      case 'company':
        return (
          <Button size="small" onClick={() => setEditing(true)}>
            Edit details
          </Button>
        );
      case 'tenant':
        return s.state === 'done' ? (
          <Link className="btn small secondary" to={portal}>
            Open their portal
          </Link>
        ) : (
          <span className="step-actions tenant-actions">
            {data.data?.tenantCreation?.on && (
              <CreateTenant
                suggested={data.data.tenantCreation.suggested ?? ''}
                onCreate={(domain) =>
                  run(async () => {
                    const r = await api.createTenant(id, domain);
                    if (r.warning) setError(r.warning);
                    return r;
                  })
                }
              />
            )}
            {data.data?.tenantCreation?.on && <span className="muted small-print">or link one that’s there already</span>}
            <TenantLink options={tenants.data ?? []} onLink={(domain) => run(() => api.updateCustomer(id, { domain }))} />
          </span>
        );
      case 'numbers':
      case 'people':
      case 'phones': {
        const page = { numbers: 'numbers', people: 'people', phones: 'phones' }[s.step];
        return (
          <span className="step-actions">
            {portal && s.state !== 'waiting' && (
              <Link className="btn small secondary" to={`${portal}/${page}`}>
                {s.step === 'numbers' ? 'See numbers' : s.step === 'people' ? 'Add people' : 'Add phones'}
              </Link>
            )}
            {s.state !== 'waiting' && (
              <Button size="small" variant="ghost" onClick={() => run(() => api.skipStep(id, s.step as 'numbers' | 'people' | 'phones', s.state !== 'skipped'))}>
                {s.state === 'skipped' ? 'Not skipped after all' : 'Not needed'}
              </Button>
            )}
          </span>
        );
      }
      case 'admin':
        return c.tenant && s.state !== 'done' ? (
          <AdminInvite
            contactName={c.contactName}
            contactEmail={c.contactEmail}
            onInvite={async (email, name) => {
              setError('');
              try {
                setInvite(await api.invite(c.tenant!, { email, name, role: 'admin' }));
                data.reload();
              } catch (e) {
                setError(e instanceof Error ? e.message : String(e));
              }
            }}
          />
        ) : c.tenant ? (
          <Link className="btn small secondary" to={`${portal}/team`}>
            Portal access
          </Link>
        ) : null;
      case 'live':
        // Archive only: brought back from its own card, below.
        if (c.status === 'archive_only') return null;
        return c.status === 'active' ? (
          <Button size="small" variant="ghost" onClick={() => run(() => api.updateCustomer(id, { status: 'onboarding' }))}>
            Back to setting up
          </Button>
        ) : (
          <Button size="small" variant="primary" disabled={!c.tenant} onClick={() => run(() => api.updateCustomer(id, { status: 'active' }))}>
            Go live
          </Button>
        );
    }
  };

  return (
    <>
      <PageHeader
        eyebrow={
          <>
            <Link to="/admin/customers">Customers</Link> · <span className="mono">{c.tenant ?? 'no tenant yet'}</span>
          </>
        }
        title={c.name}
        chips={<Chip tone={STATUS[c.status].tone}>{STATUS[c.status].label}</Chip>}
        actions={
          portal ? (
            <>
              {/* Their calls and Patches with each leg and how it ended (3126). */}
              <Link className="btn secondary" to={`/admin/customers/${encodeURIComponent(c.id)}/sessions`}>
                <Icon name="phone" size={17} /> Live sessions
              </Link>
              <Link className="btn primary" to={portal}>
                <Icon name="eye" size={17} /> Open their portal
              </Link>
            </>
          ) : undefined
        }
      />
      <div className="content">
        {error && <Notice tone="crit">{error}</Notice>}
        {invite && <ShareLink result={invite} />}
        {c.tenant && !data.data.writable && (
          <Notice tone="warn" icon="lock">
            {c.tenant} is read-only right now (Stitch is in read-only mode), so people and phones can be seen but not changed here.
          </Notice>
        )}
        <Card title={`Getting ${c.name} set up`} action={<span className="muted">{`${done} of ${steps.length} done`}</span>}>
          <ol className="onboard">
            {steps.map((s, i) => (
              <li key={s.step} className={`onboard-step ${s.state}`}>
                <span className="onboard-mark" aria-hidden="true">
                  {s.state === 'done' ? <Icon name="check" size={16} strokeWidth={2.5} /> : s.state === 'skipped' ? '–' : <Icon name={STEP_ICON[s.step]} size={16} />}
                </span>
                <div className="onboard-body">
                  <div className="onboard-title">
                    <strong>
                      {i + 1}. {s.title}
                    </strong>
                    <span className="sr-only">: {s.state}</span>
                    {s.state === 'waiting' && <Chip small>Waiting</Chip>}
                  </div>
                  <span className="muted">{s.detail}</span>
                </div>
                <div className="onboard-action">{action(s)}</div>
              </li>
            ))}
          </ol>
        </Card>
        {/* 1.13.0: every feature, on or off, why, and who turns it on; the Updates card carries the rollouts behind four of them. */}
        <FeaturesCard customerId={c.id} name={c.name} />
        {/* 1.12.33: what this customer still needs (App calling, voicemail emails, old voicemail, Patch rings). */}
        <UpdatesCard customerId={c.id} name={c.name} linked={Boolean(c.tenant)} settingUp={c.status === 'onboarding'} />
        <NumbersPolicyCard customerId={c.id} hasTenant={Boolean(c.tenant)} />
        <MessagingSeatsCard customerId={c.id} />
        <TextingCard customerId={c.id} texting={data.data.texting} onChange={data.reload} />
        <CallsAbroadCard customerId={c.id} hasTenant={Boolean(c.tenant)} />
        <TeampaceCard customerId={c.id} />
        <RegistrationStaffCard customerId={c.id} />
        <ArchiveOnlyCard customer={c} onChange={(status) => run(() => api.updateCustomer(id, { status }))} />
        {c.notes && (
          <Card title="Notes">
            <p className="prewrap card-body">{c.notes}</p>
          </Card>
        )}
      </div>
      {editing && (
        <EditCustomer
          detail={data.data}
          onClose={() => setEditing(false)}
          onSave={async (set) => {
            await run(() => api.updateCustomer(id, set));
            setEditing(false);
          }}
        />
      )}
    </>
  );
}

/** Creating the tenant from here (system writes on): its domain, then Create. */
function CreateTenant({ suggested, onCreate }: { suggested: string; onCreate: (domain: string) => Promise<void> }) {
  const [domain, setDomain] = useState(suggested);
  const [busy, setBusy] = useState(false);
  const ok = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]*[a-z0-9])?)+$/.test(domain.trim().toLowerCase());
  return (
    <span className="tenant-row">
      <input className="input small mono" aria-label="New tenant’s domain" placeholder="company.example.com" value={domain} onChange={(e) => setDomain(e.target.value.trim().toLowerCase())} />
      <Button
        size="small"
        variant="primary"
        disabled={!ok || busy}
        onClick={async () => {
          setBusy(true);
          try {
            await onCreate(domain.trim().toLowerCase());
          } finally {
            setBusy(false);
          }
        }}
      >
        {busy ? 'Creating…' : 'Create'}
      </Button>
    </span>
  );
}

function TenantLink({ options, onLink }: { options: { domain: string; name: string }[]; onLink: (domain: string) => void }) {
  const [domain, setDomain] = useState('');
  return (
    <span className="tenant-row">
      <select className="input small" value={domain} onChange={(e) => setDomain(e.target.value)} aria-label="Phone system tenant">
        <option value="">Choose the tenant…</option>
        {options.map((t) => (
          <option key={t.domain} value={t.domain}>
            {t.name} ({t.domain})
          </option>
        ))}
      </select>
      <Button size="small" variant="primary" disabled={!domain} onClick={() => onLink(domain)}>
        Link
      </Button>
    </span>
  );
}

function AdminInvite({ contactName, contactEmail, onInvite }: { contactName: string; contactEmail: string; onInvite: (email: string, name: string) => Promise<void> }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState(contactEmail);
  const [name, setName] = useState(contactName);
  const [busy, setBusy] = useState(false);
  return (
    <>
      <Button size="small" variant="primary" onClick={() => setOpen(true)}>
        Invite their admin
      </Button>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        title="Invite their admin"
        subtitle="They choose a password and can then invite the rest of their team."
        foot={
          <>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={!email.trim() || busy}
              onClick={async () => {
                setBusy(true);
                await onInvite(email.trim(), name.trim());
                setBusy(false);
                setOpen(false);
              }}
            >
              Invite
            </Button>
          </>
        }
      >
        <Field label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-autofocus />
        <Field label="Name" value={name} onChange={(e) => setName(e.target.value)} />
      </Dialog>
    </>
  );
}

function EditCustomer({
  detail,
  onClose,
  onSave
}: {
  detail: CustomerDetail;
  onClose: () => void;
  onSave: (set: { name: string; contactName: string; contactEmail: string; notes: string }) => Promise<void>;
}) {
  const c = detail.customer;
  const [f, setF] = useState({ name: c.name, contactName: c.contactName, contactEmail: c.contactEmail, notes: c.notes });
  const [error, setError] = useState('');
  return (
    <Dialog
      open
      onClose={onClose}
      title="Company details"
      foot={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!f.name.trim()}
            onClick={async () => {
              setError('');
              try {
                await onSave(f);
              } catch (e) {
                setError(e instanceof Error ? e.message : String(e));
              }
            }}
          >
            Save
          </Button>
        </>
      }
    >
      <Field label="Company name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
      <Field label="Contact name" value={f.contactName} onChange={(e) => setF({ ...f, contactName: e.target.value })} />
      <Field label="Contact email" type="email" value={f.contactEmail} onChange={(e) => setF({ ...f, contactEmail: e.target.value })} />
      <TextAreaField label="Notes (Stitch only)" value={f.notes} rows={4} onChange={(e) => setF({ ...f, notes: e.target.value })} />
      {error && <Notice tone="crit">{error}</Notice>}
    </Dialog>
  );
}

/**
 * Texting from Messages: off until Stitch staff mark the company registered, once its 10DLC brand
 * and campaign (or toll-free verification) is approved. Texts still come in before then.
 */
function TextingCard({ customerId, texting, onChange }: { customerId: string; texting: CustomerTexting | undefined; onChange: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  /** What was just chosen (and then what Stitch answered), ahead of the page loading again. */
  const [local, setLocal] = useState<CustomerTexting | null>(null);
  const t = local ?? texting;
  const on = Boolean(t?.registered);
  return (
    <Card title="Texting" action={<Chip tone={on ? 'ok' : 'neutral'}>{on ? 'Allowed anyway' : 'Follows its registration'}</Chip>}>
      <div className="card-body section-stack">
        <label className="check">
          <input
            type="checkbox"
            checked={on}
            disabled={busy || !t}
            onChange={async (e) => {
              const registered = e.target.checked;
              setBusy(true);
              setError('');
              setLocal({ registered, at: null, by: null });
              try {
                setLocal((await api.setCustomerTexting(customerId, registered)).texting);
                onChange();
              } catch (err) {
                setLocal(null);
                setError(err instanceof Error ? err.message : String(err));
              } finally {
                setBusy(false);
              }
            }}
          />
          <span>
            Allowed to text anyway (registered outside Stitch)
            <span className="hint">
              {' '}
              For a brand and campaign (or toll-free verification) approved at Ahoi by hand. Every number can send while this is on. Companies that register in Stitch (Settings → Texting registration) don’t need it: each number’s status follows their registration. Sending also needs Spending on (System → Carrier).
            </span>
          </span>
        </label>
        {on && t?.at && (
          <p className="muted">
            Switched on by {t.by ?? 'Stitch staff'} on {dateOnly(t.at)}.
          </p>
        )}
        {error && <Notice tone="crit">{error}</Notice>}
      </div>
    </Card>
  );
}

// --- Messaging-only people: how many the company's plan includes (apps/api/src/plan.ts) ---------------

/**
 * People who sign in without a phone extension and use Messages only. Admins and anyone with an
 * extension don't count; open invitations without one do. Lowering it removes nobody.
 */
function MessagingSeatsCard({ customerId }: { customerId: string }) {
  const data = useData(() => api.messagingSeats(customerId), [customerId]);
  const [value, setValue] = useState<string | null>(null);
  const [saved, setSaved] = useState('');
  const [error, setError] = useState('');
  const [declining, setDeclining] = useState<SeatRequestView | null>(null);
  if (data.error) return null;
  const s = data.data?.seats;
  const shown = value ?? String(s?.allowance ?? '');
  const n = Number(shown);
  const bad = !/^\d{1,5}$/.test(shown) || n > 10_000 ? 'A number from 0 to 10,000.' : '';
  const requests = data.data?.requests ?? [];
  const waiting = requests.find((r) => r.status === 'waiting');
  const answered = requests.filter((r) => r.status !== 'waiting').slice(0, 3);
  const save = async (allowance: number | null) => {
    setError('');
    try {
      const r = await api.setMessagingSeats(customerId, allowance);
      setValue(null);
      setSaved(r.request ? 'Saved. Their request is answered.' : 'Saved.');
      data.reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };
  return (
    <Card title="Messaging-only people" id="seats" action={s ? <Chip tone={s.used >= s.allowance ? 'warn' : 'neutral'}>{`${s.used} of ${s.allowance} taken`}</Chip> : undefined}>
      <div className="card-body section-stack">
        {!s ? (
          <Loading label="Loading" />
        ) : (
          <>
            {waiting && (
              <Notice tone="brand" icon="users" title={`${waiting.askedBy || 'Their admin'} asked for ${waiting.more} more on ${monthDay(waiting.askedAt)}`}>
                <div className="seat-ask">
                  {waiting.note && <p className="seat-ask-note">{waiting.note}</p>}
                  <span>Raising the number below answers the request; they see it on Portal access.</span>
                  <div className="wizard-actions">
                    <Button variant="primary" size="small" disabled={s.allowance + waiting.more > 10_000} onClick={() => void save(s.allowance + waiting.more)}>
                      Raise to {s.allowance + waiting.more}
                    </Button>
                    <Button size="small" variant="ghost" onClick={() => setDeclining(waiting)}>
                      Decline…
                    </Button>
                  </div>
                </div>
              </Notice>
            )}
            <p className="muted">
              People who sign in without a phone extension and use Messages only; nothing is set up on the phone system for them. Admins and anyone with an extension don’t count. Open invitations do.
            </p>
            <Field
              label="Up to this many"
              inputMode="numeric"
              value={shown}
              onChange={(e) => (setValue(e.target.value.replace(/\D/g, '')), setSaved(''))}
              error={bad || undefined}
              hint={`${plural(s.members, 'person', 'people')} and ${plural(s.invited, 'invitation')} now. ${s.isDefault ? `Stitch’s default (${s.defaultAllowance}).` : `Stitch’s default is ${s.defaultAllowance}.`} Lowering it removes nobody: it stops new ones.`}
            />
            <div className="wizard-actions">
              <Button variant="primary" disabled={Boolean(bad) || value === null || (!s.isDefault && n === s.allowance)} onClick={() => void save(n)}>
                Save
              </Button>
              {!s.isDefault && (
                <Button variant="ghost" onClick={() => void save(null)}>
                  Use the default ({s.defaultAllowance})
                </Button>
              )}
              {saved && <span className="muted">{saved}</span>}
            </div>
            {answered.length > 0 && (
              <ul className="seat-history" aria-label="Earlier requests">
                {answered.map((r) => {
                  const said = seatRequestText(r);
                  return (
                    <li key={r.id}>
                      <Chip small tone={said.tone}>
                        {said.line}
                      </Chip>{' '}
                      <span className="muted">
                        {r.status === 'done' ? `raised to ${r.allowanceAfter} by ${r.answeredBy ?? 'Stitch staff'}` : `by ${r.answeredBy ?? 'Stitch staff'}${r.reason ? `: “${r.reason}”` : ''}`}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
            {error && <Notice tone="crit">{error}</Notice>}
          </>
        )}
      </div>
      {declining && (
        <DeclineSeatsDialog
          request={declining}
          onClose={() => setDeclining(null)}
          onDecline={async (reason) => {
            await api.declineSeatRequest(declining.id, reason);
            setDeclining(null);
            setSaved('Declined. They see your reason on Portal access.');
            data.reload();
          }}
        />
      )}
    </Card>
  );
}

/** Declining a request for more: why, in words their admin sees on Portal access. */
function DeclineSeatsDialog({ request, onClose, onDecline }: { request: SeatRequestView; onClose: () => void; onDecline: (reason: string) => Promise<void> }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  return (
    <Dialog
      open
      onClose={onClose}
      title={`Decline ${request.more} more?`}
      subtitle="Their admin sees your reason on Portal access, and the decline is in their Activity."
      foot={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="danger"
            disabled={!reason.trim() || busy}
            onClick={async () => {
              setBusy(true);
              setError('');
              try {
                await onDecline(reason.trim());
              } catch (e) {
                setError(e instanceof Error ? e.message : String(e));
                setBusy(false);
              }
            }}
          >
            Decline
          </Button>
        </>
      }
    >
      <div className="stack">
        <TextAreaField label="Why" rows={3} maxLength={500} value={reason} data-autofocus placeholder="For example: call us to move to a bigger plan" onChange={(e) => setReason(e.target.value)} />
        {error && <Notice tone="crit">{error}</Notice>}
      </div>
    </Dialog>
  );
}

/** Whether the company's admins can get numbers themselves, and how many a month. */
function NumbersPolicyCard({ customerId, hasTenant }: { customerId: string; hasTenant: boolean }) {
  const data = useData(() => api.numbersPolicy(customerId), [customerId]);
  const [canOrder, setCanOrder] = useState<boolean | null>(null);
  const [limit, setLimit] = useState<string | null>(null);
  const [saved, setSaved] = useState('');
  const [error, setError] = useState('');
  if (data.error) return null;
  const p = data.data?.policy;
  const on = canOrder ?? p?.canOrder ?? true;
  const max = limit ?? String(p?.monthlyLimit ?? 10);
  const n = Number(max);
  const bad = !/^\d{1,3}$/.test(max) || n > 500 ? 'A number from 0 to 500.' : '';
  const changed = p && (on !== p.canOrder || n !== p.monthlyLimit);
  return (
    <Card title="Getting numbers">
      <div className="card-body section-stack">
        {!data.data ? (
          <Loading label="Loading" />
        ) : (
          <>
            {!data.data.carrier && <Notice icon="lock">No carrier is connected on this server, so nobody can get numbers here yet.</Notice>}
            <label className="check">
              <input type="checkbox" checked={on} onChange={(e) => (setCanOrder(e.target.checked), setSaved(''))} />
              <span>
                Their admins can get new numbers themselves
                <span className="hint"> Stitch staff always can, without a limit.</span>
              </span>
            </label>
            {on && (
              <Field
                label="Up to this many a month"
                inputMode="numeric"
                value={max}
                onChange={(e) => (setLimit(e.target.value.replace(/\D/g, '')), setSaved(''))}
                error={bad || undefined}
                hint={`${plural(data.data.usedThisMonth, 'number')} bought this month${hasTenant ? '' : ' (no tenant linked yet)'}. Every new number stays on their bill for at least ${data.data.minHoldDays} days.`}
              />
            )}
            <div className="wizard-actions">
              <Button
                variant="primary"
                disabled={!changed || Boolean(bad)}
                onClick={async () => {
                  setError('');
                  try {
                    await api.setNumbersPolicy(customerId, { canOrder: on, monthlyLimit: n });
                    setCanOrder(null);
                    setLimit(null);
                    setSaved('Saved.');
                    data.reload();
                  } catch (e) {
                    setError(e instanceof Error ? e.message : String(e));
                  }
                }}
              >
                Save
              </Button>
              {saved && <span className="muted">{saved}</span>}
            </div>
            {error && <Notice tone="crit">{error}</Notice>}
          </>
        )}
      </div>
    </Card>
  );
}

/**
 * Archive only (1.13.0, Teampace 2717): a company whose service ended keeps Call history (from the
 * archive), Storage and retention, legal holds, Activity and Billing; nothing else, and Stitch never
 * asks its phone system anything for it. Its call rows leave Stitch's database 30 days later, once
 * each is in the archive. Asks first; bringing it back is one press.
 */
function ArchiveOnlyCard({ customer: c, onChange }: { customer: CustomerSummary; onChange: (status: 'archive_only' | 'active' | 'onboarding') => Promise<void> }) {
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  const on = c.status === 'archive_only';
  const change = async (status: 'archive_only' | 'active' | 'onboarding') => {
    setBusy(true);
    try {
      await onChange(status);
    } finally {
      setBusy(false);
      setAsking(false);
    }
  };
  return (
    <Card title="Archive only" action={on ? <Chip tone="neutral">Since {c.archiveOnlyAt ? dateOnly(c.archiveOnlyAt) : 'now'}</Chip> : undefined}>
      <div className="card-body stack">
        {on ? (
          <>
            <p className="muted">
              {c.name}’s people can sign in to Call history (from the archive), Storage and retention, legal holds, Activity and Billing. Nothing else, and Stitch asks its phone
              system nothing.{c.archiveOnlyBy ? ` Made archive only by ${c.archiveOnlyBy}.` : ''} Its calls leave Stitch’s database 30 days after that, once each one is in the
              archive; the archive keeps them as long as the company’s retention says.
            </p>
            <div className="settings-actions">
              <Button variant="primary" disabled={busy || !c.tenant} onClick={() => void change(c.liveAt ? 'active' : 'onboarding')}>
                {busy ? 'Bringing it back…' : 'Bring it back'}
              </Button>
            </div>
          </>
        ) : (
          <>
            <p className="muted">
              For a company whose service has ended: its people keep Call history, Storage and retention, legal holds, Activity and Billing; calls in the app, Messages and
              everything on the phone system stop for it in Stitch. Its tenant and numbers on the phone system aren’t changed.
            </p>
            <div className="settings-actions">
              <Button variant="ghost" disabled={busy} onClick={() => setAsking(true)}>
                Make archive only
              </Button>
            </div>
          </>
        )}
      </div>
      <Dialog
        open={asking}
        onClose={() => setAsking(false)}
        title={`Make ${c.name} archive only?`}
        foot={
          <>
            <Button variant="ghost" onClick={() => setAsking(false)}>
              Cancel
            </Button>
            <Button variant="danger" disabled={busy} onClick={() => void change('archive_only')}>
              {busy ? 'Making it archive only…' : 'Yes, make it archive only'}
            </Button>
          </>
        }
      >
        <div className="stack">
          <p>
            Its people will see only Call history, Storage and retention, legal holds, Activity and Billing. Calls in the app, Messages, Meetings and every phone system page stop at
            once, and Stitch asks the phone system nothing more for {c.name}.
          </p>
          <p className="muted">
            In 30 days, its calls leave Stitch’s database once each one is in the archive (only if Stitch Admin → System → Retention has that switched on). You can bring it back
            any time before or after.
          </p>
        </div>
      </Dialog>
    </Card>
  );
}
